import type { HTMLAttributes } from "react";
import { styled, keyframes } from "@pigment-css/react";
import { ChevronRight } from "@acto/web-common/components";
import { Flex } from "../../primitives";

const RowElement = ({ onOpen, ...props }: HTMLAttributes<HTMLElement> & { onOpen?: () => void }) =>
  onOpen ? <button type="button" onClick={onOpen} {...props} /> : <div {...props} />;

const ProductGroupRow = styled(RowElement)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.managementView.spacing[16],
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[12]} ${theme.managementView.spacing[8]} ${theme.managementView.spacing[14]}`,
  marginLeft: `-${theme.managementView.spacing[14]}`,
  width: `calc(100% + ${theme.managementView.spacing[14]})`,
  backgroundColor: "transparent",
  border: "none",
  textAlign: "left",
  transition: "background-color 0.2s",
  borderRadius: theme.managementView.radius[16],

  "&:where(button)": {
    cursor: "pointer",
  },

  "&:where(button):hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },

  "& svg": {
    width: "10px",
    height: "10px",
    color: theme.managementView.colors.neutral[56],
  },
}));

const ColorDot = styled("div")(({ theme }) => ({
  width: "24px",
  height: "12px",
  marginTop: theme.managementView.spacing[6],
  borderRadius: theme.managementView.radius[32],
  flexShrink: 0,
}));

const ChevronSlot = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  marginLeft: `-${theme.managementView.spacing[4]}`,
  flexShrink: 0,
}));

const ProductGroupName = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  lineHeight: theme.font.lineHeight[20],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  flex: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const ProductGroupText = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  paddingTop: theme.managementView.spacing[2],
}));

const ProductGroupFigures = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  flexShrink: 0,
});

const ProductGroupReference = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  lineHeight: theme.managementView.font.lineHeight[164],
  color: theme.managementView.colors.neutral[56],
}));

const ProductGroupValue = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[400],
  lineHeight: theme.managementView.font.lineHeight[164],
  color: theme.managementView.colors.neutral[90],
  flexShrink: 0,
}));

const ProductGroupChangeValue = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  lineHeight: theme.managementView.font.lineHeight[164],
  color: theme.managementView.colors.neutral[56],
  flexShrink: 0,
}));

const shimmer = keyframes({
  "0%": { backgroundPosition: "-200px 0" },
  "100%": { backgroundPosition: "200px 0" },
});

const SkeletonElement = styled("div")(({ theme }) => ({
  borderRadius: theme.managementView.radius[10],
  background: `linear-gradient(90deg, ${theme.colors.neutral[200]} 0%, ${theme.colors.neutral[100]} 50%, ${theme.colors.neutral[200]} 100%)`,
  backgroundSize: "400px 100%",
  animation: `${shimmer} 1.5s ease-in-out infinite`,
}));

const SkeletonDot = styled(SkeletonElement)(({ theme }) => ({
  width: "24px",
  height: "12px",
  borderRadius: theme.managementView.radius[32],
  flexShrink: 0,
}));

const SkeletonName = styled(SkeletonElement)({
  height: "16px",
  flex: 1,
});

const SkeletonValue = styled(SkeletonElement)({
  height: "15px",
  width: "60px",
  flexShrink: 0,
});

const SkeletonChange = styled(SkeletonElement)({
  height: "15px",
  width: "40px",
  flexShrink: 0,
});

const SkeletonRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[16],
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[12]} ${theme.managementView.spacing[8]} ${theme.managementView.spacing[14]}`,
  marginLeft: `-${theme.managementView.spacing[14]}`,
  width: `calc(100% + ${theme.managementView.spacing[14]})`,
}));

export interface ProductRowView {
  key: string;
  name: string;
  /** `undefined` hides the reference line; `null` means the group has no reference. */
  externalReference?: string | null;
  formattedValue: string;
  change: number | null;
  color: string;
  onOpen?: () => void;
}

export const formatSignedPercent = (value: number) => `${Math.round(value) > 0 ? "+" : ""}${Math.round(value)}%`;

interface ProductRowListProps {
  rows: ProductRowView[];
  isLoadingMore: boolean;
}

export const ProductRowList = ({ rows, isLoadingMore }: ProductRowListProps) => (
  <Flex direction="column" gap={6}>
    {rows.map((row) => {
      const change =
        row.change !== null ? (
          <ProductGroupChangeValue>{formatSignedPercent(row.change)}</ProductGroupChangeValue>
        ) : null;

      return (
        <ProductGroupRow key={row.key} onOpen={row.onOpen}>
          <ColorDot style={{ backgroundColor: row.color }} />
          {row.externalReference === undefined ? (
            <>
              <ProductGroupName title={row.name}>{row.name}</ProductGroupName>
              <ProductGroupValue>{row.formattedValue}</ProductGroupValue>
              {change}
            </>
          ) : (
            <>
              <ProductGroupText>
                <ProductGroupName title={row.name}>{row.name}</ProductGroupName>
                {row.externalReference && <ProductGroupReference>{row.externalReference}</ProductGroupReference>}
              </ProductGroupText>
              <ProductGroupFigures>
                <ProductGroupValue>{row.formattedValue}</ProductGroupValue>
                {change}
              </ProductGroupFigures>
            </>
          )}
          {row.onOpen && (
            <ChevronSlot>
              <ChevronRight />
            </ChevronSlot>
          )}
        </ProductGroupRow>
      );
    })}
    {isLoadingMore &&
      Array.from({ length: 3 }).map((_, index) => (
        <SkeletonRow key={`skeleton-${index}`}>
          <SkeletonDot />
          <SkeletonName />
          <SkeletonValue />
          <SkeletonChange />
        </SkeletonRow>
      ))}
  </Flex>
);
